// 메인 화면.
// 작업계획서 4.2 (6~12): 상단바(앱명/알림/장바구니), 검색, 베스트 5, 카테고리 바로가기, 배너 자동 전환.
//
// 알림/장바구니 라우트는 Phase 3~4 범위. 본 라운드에서는 상단바 아이콘만 두고 클릭 시 안내한다.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Search, ShoppingCart } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  listBestProducts,
  listCategories,
  listVisibleBanners,
} from "@/features/catalog/catalogApi.ts";
import ProductCard from "@/features/catalog/ProductCard.tsx";
import BannerCarousel from "@/features/catalog/BannerCarousel.tsx";
import AllProductsSection from "@/features/catalog/AllProductsSection.tsx";
import { imageUrl } from "@/features/catalog/imageUrl.ts";
import { useCart } from "@/features/cart/useCart.ts";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";
import { Button } from "@/shared/ui";

// 메인 홈 화면 (배너/카테고리/인기상품).
export default function HomePage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const cart = useCart();
  const cartCount = cart.count;

  const banners = useQuery({ queryKey: ["catalog", "banners"], queryFn: listVisibleBanners });
  // 관리자가 베스트로 지정한 상품만 그 개수만큼 노출(빈자리 일반상품 채움 없음). 라우트 상한(20)까지 받는다.
  const bests = useQuery({ queryKey: ["catalog", "best"], queryFn: () => listBestProducts(20) });
  const cats = useQuery({ queryKey: ["catalog", "categories"], queryFn: listCategories });
  const ops = useStoreSettings();

  function onSearch() {
    const trimmed = q.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="flex flex-col">
      {/* 상단바: 작업계획서 6번 */}
      {/* 상단바: 높이를 콘텐츠에 맞게 축소(개선항목 1). 세로 패딩을 줄여 로고/상호 높이에 딱 맞춘다. */}
      {/* top: 안전영역만큼 내려 붙인다(상태바 스트립 바로 아래). 웹은 0 이라 기존과 동일. */}
      <header className="sticky top-[var(--safe-top)] z-10 flex items-center gap-2 border-b border-ink-200 bg-surface px-4 py-1.5">
        {/* 상단바 로고 + 상호. 홈 상단바는 기존 로고(logo-home.png)를 쓰고,
            앱 설치 아이콘/스플래시는 icon-512.png(앱 아이콘)로 분리한다. */}
        <Link to="/" className="flex items-center gap-2 text-xl font-bold text-brand-700">
          <img
            src="/icons/logo-home.png"
            alt=""
            width={36}
            height={36}
            className="h-9 w-9 rounded-lg"
          />
          열린축산
        </Link>
        {/* 상호 옆 영업 상태 배지(개선): 영업중=초록, 영업종료=빨강. 운영설정 로드 후 노출. */}
        {ops.openStatus ? (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              ops.openStatus.isOpen
                ? "bg-success-50 text-success-700"
                : "bg-danger-50 text-danger-700"
            }`}
          >
            {ops.openStatus.isOpen ? "영업중" : "영업종료"}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-0.5">
          {/* 알림센터(Phase 4-D /me/notifications)로 이동. 비로그인 시 ProtectedRoute가 로그인으로 유도. */}
          <Link
            to="/me/notifications"
            className="inline-flex min-h-touch min-w-touch items-center justify-center rounded text-ink-700 hover:bg-ink-100"
            aria-label="알림"
          >
            <Bell size={22} aria-hidden="true" />
          </Link>
          <Link
            to="/cart"
            className="relative inline-flex min-h-touch min-w-touch items-center justify-center rounded text-ink-700 hover:bg-ink-100"
            aria-label={cartCount > 0 ? `장바구니 (${cartCount}종)` : "장바구니"}
          >
            <ShoppingCart size={22} aria-hidden="true" />
            {cartCount > 0 ? (
              <span className="absolute right-1 top-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[11px] font-bold text-white">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            ) : null}
          </Link>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-6 px-4 py-4">
        {/* 검색 칸: 작업계획서 9번 */}
        <section>
          <label htmlFor="home-search" className="sr-only">
            상품명 검색
          </label>
          <div className="flex gap-2">
            <input
              id="home-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearch();
              }}
              placeholder="찾으시는 상품을 입력하세요"
              // min-w-0: flex 자식 기본 min-width:auto 때문에 input 이 placeholder 고유폭 아래로
              // 줄어들지 않아 옆 버튼과 합쳐져 가로 넘침이 생기던 것을 차단(어떤 화면폭에서도 축소 허용).
              className="min-h-[3rem] min-w-0 flex-1 rounded border border-ink-300 bg-surface px-4 text-base text-ink-900 placeholder:text-ink-500 focus-visible:border-brand-600"
            />
            {/* shrink-0 + 줄바꿈 금지: 검색 버튼은 항상 한 줄/고정폭 유지, 좁아지면 input 쪽이 줄어든다. */}
            <Button type="button" onClick={onSearch} className="shrink-0 whitespace-nowrap">
              <Search size={18} aria-hidden="true" />
              검색
            </Button>
          </div>
        </section>

        {/* 배너: 작업계획서 12번 */}
        {banners.isLoading ? (
          <div className="aspect-[16/7] w-full animate-pulse rounded-lg bg-ink-100" />
        ) : banners.data && banners.data.length > 0 ? (
          <BannerCarousel banners={banners.data} />
        ) : null}

        {/* 베스트 5: 작업계획서 10번 */}
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-bold">베스트 상품</h2>
          {bests.isLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="aspect-square animate-pulse rounded-lg bg-ink-100" />
              ))}
            </div>
          ) : bests.data && bests.data.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {bests.data.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          ) : (
            <p className="rounded border border-dashed border-ink-300 p-6 text-center text-sm text-ink-600">
              곧 추천 상품을 만나보실 수 있습니다.
            </p>
          )}
        </section>

        {/* 카테고리 바로가기: 작업계획서 11번 */}
        <section className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-bold">카테고리</h2>
            <Link to="/category" className="text-sm text-ink-600 hover:text-brand-700">
              전체 보기
            </Link>
          </div>
          {cats.isLoading ? (
            <div className="grid grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="aspect-square animate-pulse rounded-lg bg-ink-100" />
              ))}
            </div>
          ) : cats.data && cats.data.length > 0 ? (
            <ul className="grid grid-cols-4 gap-3">
              {cats.data.slice(0, 8).map((c) => {
                const iconUrl = imageUrl(c.iconImageKey);
                return (
                  <li key={c.id}>
                    <Link
                      to={`/category/${c.id}`}
                      className="flex flex-col items-center gap-1.5 rounded-lg p-2 hover:bg-ink-100"
                    >
                      <div className="aspect-square w-full overflow-hidden rounded-lg bg-ink-100">
                        {iconUrl ? (
                          <img src={iconUrl} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                        ) : null}
                      </div>
                      {/* 카테고리명은 말줄임 없이 전체 노출. 한글은 단어 단위로 줄바꿈(break-keep)해
                          긴 이름도 깔끔히 여러 줄로 보이게 한다(개선항목 3). */}
                      <span className="text-center text-xs leading-tight break-keep">{c.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="rounded border border-dashed border-ink-300 p-6 text-center text-sm text-ink-600">
              카테고리가 곧 등록됩니다.
            </p>
          )}
        </section>

        {/* 전체 상품: 카테고리 아래에 판매 중인 모든 상품(품절 제외)을 정렬/무한스크롤로 노출 */}
        <AllProductsSection />
      </main>
    </div>
  );
}
